import { useState, type FormEvent } from 'react';
import {
  api,
  BladApi,
  type KartaZlecenia,
  type PozycjaSlownika,
  type RodzajNaprawy,
  type Zlecenie,
} from '../api';
import { useIdOperacji } from '../operacje';
import { PolePodpisu } from './PolePodpisu';
import { IkonaPlus } from './Icons';
import { Karta, Komunikat, PoleTekstowe, Przycisk, PrzyciskMarki } from './ui';

/**
 * Protokół serwisowy — ekran, na którym rozstrzyga się test 90 sekund
 * (CLAUDE.md §1 zasada 7).
 *
 * Dlatego wszystko, co się da, jest **klikane, nie pisane**: rodzaj naprawy to cztery
 * pozycje z druku, czynności to chipy ze słownika branżowego. Opis jest opcjonalny
 * i istnieje tylko na wypadek, gdy słownik nie wystarczy.
 *
 * Wymagane są dwie rzeczy: **co najmniej jedna czynność i podpis** (zasada 2).
 * Podpisanie zamyka zlecenie — to strzałka z diagramu w docs/model-danych.md §5.
 */

const RODZAJE: { kod: RodzajNaprawy; nazwa: string; opis: string }[] = [
  { kod: 'GWARANCYJNA', nazwa: 'Gwarancyjna', opis: 'płaci producent' },
  { kod: 'POGWARANCYJNA', nazwa: 'Pogwarancyjna', opis: 'płaci klient' },
  { kod: 'NIEOBJETA_GWARANCJA', nazwa: 'Nieobjęta gwarancją', opis: 'uszkodzenie z winy użytkownika' },
  { kod: 'REGULACJA', nazwa: 'Regulacja', opis: 'bez wymiany części' },
];

interface Pozycja {
  numerCzesci: string;
  opis: string;
  ilosc: number;
  cena: number;
}

export function FormularzProtokolu({
  zlecenie,
  czynnosciSlownik,
  onZapisano,
  onAnuluj,
}: {
  zlecenie: Zlecenie;
  czynnosciSlownik: PozycjaSlownika[];
  onZapisano: (karta: KartaZlecenia) => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();

  const [rodzajNaprawy, setRodzajNaprawy] = useState<RodzajNaprawy>('POGWARANCYJNA');
  const [czynnosci, setCzynnosci] = useState<string[]>([]);
  const [opis, setOpis] = useState('');
  const [czasNaprawyH, setCzasNaprawyH] = useState('1');
  const [zalecenia, setZalecenia] = useState('');
  const [czesci, setCzesci] = useState<Pozycja[]>([]);
  const [podpis, setPodpis] = useState<string | null>(null);
  const [kto, setKto] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  const gotowe = czynnosci.length > 0 && podpis && kto.trim();

  function przelacz(kod: string): void {
    setCzynnosci((poprzednie) =>
      poprzednie.includes(kod) ? poprzednie.filter((c) => c !== kod) : [...poprzednie, kod],
    );
  }

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!gotowe || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      const nowa = await api.utworzProtokol(zlecenie.id, {
        clientOperationId: operacja.biezacy(),
        rodzajNaprawy,
        czynnosci,
        opis: opis.trim() || undefined,
        czasNaprawyH: Number(czasNaprawyH.replace(',', '.')) || 0,
        zalecenia: zalecenia.trim() || undefined,
        podpisKlienta: { image: podpis, kto: kto.trim() },
        czesci: czesci
          .filter((pozycja) => pozycja.opis.trim())
          .map((pozycja) => ({
            numerCzesci: pozycja.numerCzesci.trim() || undefined,
            opis: pozycja.opis.trim(),
            ilosc: pozycja.ilosc,
            cena: pozycja.cena,
          })),
      });
      operacja.zakoncz();
      onZapisano(nowa);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać protokołu.');
      setZapis(false);
    }
  }

  const zmien = (indeks: number, zmiana: Partial<Pozycja>): void =>
    setCzesci((p) => p.map((pozycja, i) => (i === indeks ? { ...pozycja, ...zmiana } : pozycja)));

  return (
    <form onSubmit={wyslij} noValidate>
      <Karta
        tytul="Wykonane czynności — kliknij, nie pisz"
        wyroznienie
        akcje={<span className="t-small text-n-600">wymagana przynajmniej jedna</span>}
      >
        <div className="flex flex-wrap gap-7">
          {czynnosciSlownik.map((czynnosc) => {
            const wybrana = czynnosci.includes(czynnosc.kod);
            return (
              <button
                key={czynnosc.kod}
                type="button"
                aria-pressed={wybrana}
                onClick={() => przelacz(czynnosc.kod)}
                // Zaznaczenie niesie kolor marki — to oznaczenie wyboru, nie akcja.
                className={`border px-13 text-left ${
                  wybrana
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-n-300 bg-surface text-n-700 hover:bg-n-50'
                }`}
              >
                <span className="t-label">{czynnosc.nazwa}</span>
              </button>
            );
          })}
        </div>

        <label className="mt-14 block">
          <span className="t-label mb-5 block text-n-700">
            Opis <span className="t-small text-n-500">— tylko gdy czynności nie wystarczą</span>
          </span>
          <textarea
            value={opis}
            onChange={(z) => setOpis(z.target.value)}
            rows={2}
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </label>
      </Karta>

      <Karta tytul="Rodzaj naprawy">
        <div className="grid gap-9 sm:grid-cols-2">
          {RODZAJE.map((rodzaj) => {
            const wybrany = rodzajNaprawy === rodzaj.kod;
            return (
              <button
                key={rodzaj.kod}
                type="button"
                aria-pressed={wybrany}
                onClick={() => setRodzajNaprawy(rodzaj.kod)}
                className={`flex flex-col items-start border px-13 py-9 text-left ${
                  wybrany
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-n-300 bg-surface text-n-700 hover:bg-n-50'
                }`}
              >
                <span className="t-body-strong">{rodzaj.nazwa}</span>
                <span className="t-small text-n-600">{rodzaj.opis}</span>
              </button>
            );
          })}
        </div>

        {rodzajNaprawy === 'GWARANCYJNA' && (
          <div className="mt-13">
            {/* Uwaga wprost z papierowego druku. */}
            <Komunikat
              rodzaj="info"
              tresc="Naprawa gwarancyjna — pamiętaj o wpisie do karty gwarancyjnej. Pozycji do faktury nie proponujemy."
            />
          </div>
        )}
      </Karta>

      <Karta tytul="Czas i zalecenia">
        <div className="grid gap-13 sm:grid-cols-2">
          <PoleTekstowe
            type="number"
            min={0}
            step="0.25"
            etykieta="Czas naprawy (h)"
            podpowiedz={'policzony od „Zaczynam”, poprawialny'}
            value={czasNaprawyH}
            onChange={(z) => setCzasNaprawyH(z.target.value)}
          />
        </div>
        <label className="mt-13 block">
          <span className="t-label mb-5 block text-n-700">Zalecenia dla klienta</span>
          <textarea
            value={zalecenia}
            onChange={(z) => setZalecenia(z.target.value)}
            rows={2}
            placeholder="np. wymienić pasek przy najbliższym przeglądzie"
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </label>
      </Karta>

      <Karta
        tytul={`Zużyte części · ${czesci.length}`}
        akcje={
          <Przycisk
            type="button"
            onClick={() => setCzesci((p) => [...p, { numerCzesci: '', opis: '', ilosc: 1, cena: 0 }])}
            ikona={<IkonaPlus rozmiar={16} />}
          >
            Dodaj pozycję
          </Przycisk>
        }
      >
        {czesci.length === 0 ? (
          <p className="t-small text-n-600">Brak — regulacja bez wymiany części też jest naprawą.</p>
        ) : (
          czesci.map((pozycja, indeks) => (
            <div
              key={indeks}
              className="mb-13 grid gap-13 last:mb-0 sm:grid-cols-[1fr_2fr_auto_auto_auto]"
            >
              <PoleTekstowe
                etykieta="Numer części"
                value={pozycja.numerCzesci}
                onChange={(z) => zmien(indeks, { numerCzesci: z.target.value })}
                autoComplete="off"
              />
              <PoleTekstowe
                etykieta="Opis"
                value={pozycja.opis}
                onChange={(z) => zmien(indeks, { opis: z.target.value })}
                autoComplete="off"
              />
              <PoleTekstowe
                type="number"
                min={0}
                step="0.01"
                etykieta="Ilość"
                value={String(pozycja.ilosc)}
                onChange={(z) => zmien(indeks, { ilosc: Number(z.target.value) })}
              />
              <PoleTekstowe
                type="number"
                min={0}
                step="0.01"
                etykieta="Cena"
                value={String(pozycja.cena)}
                onChange={(z) => zmien(indeks, { cena: Number(z.target.value) })}
              />
              <div className="flex items-end">
                <Przycisk
                  type="button"
                  onClick={() => setCzesci((p) => p.filter((_, i) => i !== indeks))}
                >
                  Usuń
                </Przycisk>
              </div>
            </div>
          ))
        )}
      </Karta>

      <Karta
        tytul="Podpis klienta"
        akcje={<span className="t-small text-n-600">podpis zamyka zlecenie</span>}
      >
        <div className="mb-13 max-w-[420px]">
          <PoleTekstowe
            etykieta="Kto podpisuje"
            podpowiedz="imię i nazwisko osoby przy maszynie"
            value={kto}
            onChange={(z) => setKto(z.target.value)}
            autoComplete="off"
          />
        </div>
        <PolePodpisu wartosc={podpis} onZmiana={setPodpis} />
        <p className="t-small mt-10 text-n-500">
          Zapisujemy obraz podpisu, kto i kiedy podpisał, na jakiej maszynie oraz skrót
          treści protokołu z tej chwili — to jedyne, co przy sporze pokazuje, pod czym
          klient faktycznie się podpisał.
        </p>
      </Karta>

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-13">
        <PrzyciskMarki type="submit" disabled={!gotowe || zapis}>
          {zapis ? 'Zapisywanie…' : 'Podpisz i zamknij zlecenie'}
        </PrzyciskMarki>
        <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
          Anuluj
        </Przycisk>
        {!gotowe && (
          <span className="t-small text-n-500">
            Potrzebna jest przynajmniej jedna czynność i podpis.
          </span>
        )}
      </div>
    </form>
  );
}
